import type { DragEvent, FC } from "react";
import { useRef } from "react";
import type { RJSFSchema, FieldProps } from "@rjsf/utils";
import { getSchemaType, mergeObjects } from "@rjsf/utils";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import MediaSelectorField from "@/framework/KioForm/fields/MediaSelectorField/MediaSelectorField";
import { calculateCoordinates } from "@/utils/geometry";

const ImageMapField: FC<FieldProps> = ({
  schema,
  uiSchema,
  formContext,
  formData,
  onChange,
  required,
  disabled,
  readonly,
  idSchema,
  errorSchema,
  onBlur,
  onFocus,
  registry,
  autofocus,
}) => {
  const { t } = useTranslation("common");
  const { schemaUtils } = registry;

  // HACK: same hack/workaround as in DocumentRelationField.
  // When the SchemaFields onChange is called, formData somehow contains the default value
  // for background, removing selected mediaId.
  const backgroundImageRef = useRef<any>(formData?.["background"]);

  const _schema = schemaUtils.retrieveSchema(schema, formData);

  const backgroundImageFormData = { ...formData?.["background"] };
  const markersFormData: any[] = formData?.["markers"] || [];

  const backgroundImageSchema = _schema.properties?.background
    ? schemaUtils.retrieveSchema(_schema.properties?.background as RJSFSchema, backgroundImageFormData)
    : null;
  const markersSchema = _schema.properties?.markers
    ? schemaUtils.retrieveSchema(_schema.properties?.markers as RJSFSchema, markersFormData)
    : null;

  const errorPrefix = "kioForm.fields.ImageMapField.errors";

  function* schemaErrors(): IterableIterator<string> {
    if (backgroundImageSchema == null) {
      yield `${errorPrefix}.missingBackgroundSchema`;
    }
    if (markersSchema == null) {
      yield `${errorPrefix}.missingMarkersSchema`;
    } else {
      if (markersSchema.type !== "array") {
        yield `${errorPrefix}.notArray`;
      }
      const resolvedItemSchema = schemaUtils.retrieveSchema(markersSchema.items as RJSFSchema, markersFormData);
      if (getSchemaType(resolvedItemSchema) !== "object") {
        yield `${errorPrefix}.itemsNotObject`;
      }
      if (!resolvedItemSchema.properties?.["coordinates"]) {
        yield `${errorPrefix}.noCoordinates`;
      }
    }
  }

  const errors = Array.from(schemaErrors());
  if (errors.length) {
    return (
      <>
        {errors.map((e) => (
          <Typography key={e} color="error">
            {t(e)}
          </Typography>
        ))}
      </>
    );
  }

  const defaultMarkersUISchema = {
    items: {
      coordinates: {
        x: {
          "ui:widget": "range",
        },
        y: {
          "ui:widget": "range",
        },
      },
    },
  };
  const markersUISchema = mergeObjects(defaultMarkersUISchema, uiSchema?.["markers"]);

  const handleImageChange = (imageFormData: any) => {
    // See above "HACK:"
    backgroundImageRef.current = imageFormData;
    onChange({
      ...formData,
      background: imageFormData,
    });
  };

  function onMarkersUpdate(markers: any[]) {
    onChange({
      ...formData,
      // see above "HACK:"
      background: backgroundImageRef.current,
      markers,
    });
  }

  function handleMarkerDrop(i: number, e: DragEvent<HTMLElement>) {
    const newCoordinates = calculateCoordinates(e);

    const newMarker = {
      ...markersFormData[i],
      coordinates: newCoordinates,
    };

    onChange({
      ...formData,
      markers: [...markersFormData.slice(0, i), newMarker, ...markersFormData.slice(i + 1)],
    });
  }

  const { SchemaField } = registry.fields;

  return (
    <>
      <MediaSelectorField
        schema={backgroundImageSchema as RJSFSchema}
        uiSchema={uiSchema?.["background"]}
        /* @RJSF5-ERR */
        idSchema={idSchema["background"]}
        formData={formData?.["background"]}
        errorSchema={errorSchema?.["background"]}
        onChange={handleImageChange}
        onBlur={onBlur}
        onFocus={onFocus}
        registry={registry}
        formContext={formContext}
        autofocus={autofocus}
        disabled={disabled}
        readonly={readonly}
        required={required}
        name={`background`}
        markers={markersFormData.map((m, i) => ({
          x: m.coordinates.x ?? 50,
          y: m.coordinates.y ?? 50,
          index: i,
        }))}
        onMarkerDrop={handleMarkerDrop}
      />
      <SchemaField
        formData={markersFormData}
        schema={markersSchema as RJSFSchema}
        registry={registry}
        formContext={formContext}
        onChange={onMarkersUpdate}
        /* @RJSF5-ERR */
        idSchema={idSchema["markers"]}
        uiSchema={markersUISchema}
        errorSchema={errorSchema?.["markers"]}
        autofocus={autofocus}
        disabled={disabled}
        readonly={readonly}
        required={required}
        onBlur={onBlur}
        onFocus={onFocus}
        name={"markers"}
      />
    </>
  );
};

export default ImageMapField;
